import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AdminTmAge — recupera le ETÀ ANAGRAFICHE da Transfermarkt (data di nascita + età)
 * per tutti i giocatori in rosa, salvandole in campi NON distruttivi
 * (tm_birthdate / tm_age). L'età TM ha priorità su scheda e stipendi.
 * Eseguire in PREVIEW (scraping), poi Esporta → Importa in Produzione.
 */
export default function AdminTmAge() {
    const [status, setStatus] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [limit, setLimit] = useState(300);
    const [importResult, setImportResult] = useState(null);

    const loadStatus = async () => {
        try {
            const { data } = await api.get("/tm-age/status");
            setStatus(data);
            return data;
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
            return null;
        }
    };

    useEffect(() => {
        loadStatus();
        const t = setInterval(loadStatus, 4000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startScrape = async () => {
        setErr("");
        try {
            await api.post("/tm-age/scrape", null, { params: { limit: Number(limit) || 0 } });
            await loadStatus();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        }
    };

    const cancelScrape = async () => {
        try { await api.post("/tm-age/scrape/cancel"); } catch { /* noop */ }
        await loadStatus();
    };

    const exportAges = async () => {
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.get("/tm-age/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "eta-transfermarkt.json";
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const importAges = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setErr("");
        setImportResult(null);
        setBusy(true);
        try {
            const parsed = JSON.parse(await file.text());
            const items = Array.isArray(parsed) ? parsed : parsed.items || [];
            const { data } = await api.post("/tm-age/import", { items });
            setImportResult(data);
            await loadStatus();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message || "File non valido");
        } finally {
            setBusy(false);
        }
    };

    const running = status?.running;
    const total = status?.players_total || 0;
    const done = status?.players_done || 0;
    const pct = total > 0 ? Math.round((100 * done) / total) : 0;

    return (
        <div data-testid="admin-tm-age">
            <div className="op-label text-tff7a1a mb-1">// ETÀ TRANSFERMARKT</div>
            <h1 className="op-heading text-3xl text-tffffff mb-2">Età anagrafiche da Transfermarkt</h1>
            <p className="text-t8f95a3 text-sm max-w-3xl mb-6 leading-relaxed">
                Recupera dal profilo Transfermarkt (link già salvato) la <strong>data di nascita</strong> e
                l'età reale di ogni giocatore in rosa. I dati vengono salvati in campi <strong>non distruttivi</strong>
                {" "}(<code>tm_birthdate</code> / <code>tm_age</code>) e l'età TM ha <strong>priorità</strong> su scheda
                giocatore e calcolo stipendi. L'età si calcola dalla data di nascita, quindi non invecchia mai di un anno.
                <br />Eseguire lo scraping in <strong>PREVIEW</strong> a lotti, poi <strong>Esporta</strong> e
                {" "}<strong>Importa in Produzione</strong>.
            </p>

            {err && <div className="op-card p-3 border-tff3b30 text-tff3b30 text-sm mb-4" data-testid="tm-age-error">{err}</div>}

            <div className="flex gap-3 mb-6 flex-wrap">
                <StatPill label="Con data di nascita" value={status?.with_birthdate ?? 0} accent="rgb(var(--tc-7ce38b))" testId="tm-age-with-birth" />
                <StatPill label="Ancora da recuperare" value={status?.remaining ?? 0} accent="rgb(var(--tc-ff7a1a))" testId="tm-age-remaining" />
                <StatPill label="Profili TM totali" value={status?.total_links ?? 0} accent="rgb(var(--tc-8f95a3))" testId="tm-age-total" />
            </div>

            {/* Scraping (PREVIEW) */}
            <div className="op-card p-4 mb-6" data-testid="tm-age-scrape-panel">
                <div className="op-label text-te8ff00 mb-2">// RECUPERO (PREVIEW)</div>
                <p className="text-t8f95a3 text-sm mb-3 leading-relaxed">
                    Recupera le età a <strong>lotti</strong> (ogni run prende solo i profili ancora senza data).
                    Se Transfermarkt rallenta o blocca, ri-lancia: riprende da dove era rimasto.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                    <label className="op-mono text-[12px] text-tc9ccd3 flex items-center gap-2">
                        Lotto:
                        <input type="number" min="0" value={limit} onChange={(e) => setLimit(e.target.value)}
                            className="op-input op-mono text-sm w-24" data-testid="tm-age-limit-input" />
                        <span className="text-t5c6370">(0 = tutti)</span>
                    </label>
                    {running ? (
                        <button onClick={cancelScrape}
                            className="op-btn border border-tff3b30 text-tff3b30 hover:bg-tff3b30 hover:text-black px-4 py-2 transition-colors"
                            data-testid="tm-age-cancel-btn">⏹ Ferma</button>
                    ) : (
                        <button onClick={startScrape}
                            className="op-btn border border-te8ff00 text-te8ff00 hover:bg-te8ff00 hover:text-black px-4 py-2 transition-colors"
                            data-testid="tm-age-start-btn">🎂 Recupera età TM</button>
                    )}
                    {status && (total > 0 || running) && (
                        <span className="op-mono text-[12px] text-tc9ccd3" data-testid="tm-age-progress">
                            {running ? "In corso… " : "Ultimo run: "}
                            {done}/{total} · {status.updated ?? 0} ok · {status.failed ?? 0} falliti
                        </span>
                    )}
                </div>
                {status && total > 0 && (
                    <div className="w-full h-1.5 bg-t22252a mt-3 rounded-full overflow-hidden">
                        <div className="h-full bg-te8ff00 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                )}
            </div>

            {/* Ponte Preview → Produzione */}
            <div className="op-card p-4 mb-6" data-testid="tm-age-bridge">
                <div className="op-label text-t2bb6ff mb-2">// PONTE PREVIEW → PRODUZIONE</div>
                <div className="flex flex-wrap items-center gap-3">
                    <button onClick={exportAges} disabled={busy}
                        className="op-btn border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff hover:text-black px-4 py-2 disabled:opacity-50 transition-colors"
                        data-testid="tm-age-export-btn">⬇ Esporta età (JSON)</button>
                    <label className="op-btn border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black px-4 py-2 cursor-pointer transition-colors"
                        data-testid="tm-age-import-label">⬆ Importa in Produzione
                        <input type="file" accept="application/json,.json" onChange={importAges} disabled={busy} className="hidden" data-testid="tm-age-import-input" />
                    </label>
                    {importResult && (
                        <span className="op-mono text-[12px] text-t7ce38b" data-testid="tm-age-import-result">
                            ✓ {importResult.received} ricevuti · {importResult.applied_roster} voci rosa · {importResult.applied_players} schede aggiornate
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatPill({ label, value, accent, testId }) {
    return (
        <div className="border border-t22252a px-4 py-3 bg-t0e1014" data-testid={testId}>
            <div className="op-label text-[10px]">{label}</div>
            <div className="op-mono font-bold text-2xl" style={{ color: accent }}>{(value ?? 0).toLocaleString("it-IT")}</div>
        </div>
    );
}
